import React, { useLayoutEffect, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material';
import { type Repository, type TimelineEntry } from '../types';
import { useAppSelector } from '../hooks';
import { selectAllTimelineCollections } from '../containers/contentSlice';
import TimelineCollectionsField, { isTimelineCollectionInputComplete, type TimelineCollectionInput } from './TimelineCollectionsField';

export interface EditTimelineDialogProps {
	open: boolean;
	timeline: TimelineEntry | null;
	repositories: Repository[];
	onClose: () => void;
	onSubmit: (payload: { id: string; name: string; collections: TimelineCollectionInput[] }) => void;
}

const EditTimelineDialog: React.FC<EditTimelineDialogProps> = ({
	open,
	timeline,
	repositories,
	onClose,
	onSubmit,
}) => {
	const [name, setName] = useState('');
	const [collections, setCollections] = useState<TimelineCollectionInput[]>([]);
	const allTimelineCollections = useAppSelector(selectAllTimelineCollections);

	useLayoutEffect(() => {
		if (!open || !timeline) {
			return;
		}
		setName(timeline.name ?? '');
		setCollections(
			allTimelineCollections
				.filter((collection) => collection.timelineId === timeline.id)
				.map(({ id, repositoryId, datePropertyId }) => ({ id, repositoryId, datePropertyId })),
		);
	}, [timeline, open]);

	const canSubmit = Boolean(name.trim()) && collections.length > 0 && collections.every(isTimelineCollectionInputComplete);

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!timeline || !canSubmit) {
			return;
		}
		onSubmit({ id: timeline.id, name: name.trim(), collections });
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="body">
			<form onSubmit={handleSubmit} autoComplete="off">
				<DialogTitle>Edit timeline</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={1}>
						<TextField
							label="Name"
							size="small"
							required
							fullWidth
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
						<TimelineCollectionsField value={collections} repositories={repositories} onChange={setCollections} />
					</Box>
				</DialogContent>
				<DialogActions>
					<Button type="button" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={!canSubmit}>
						Save
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default EditTimelineDialog;
